import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Pagination,
  Paper,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionIcon from "@mui/icons-material/Description";
import { useDispatch, useSelector } from "react-redux";
import { fetchBatchById } from "../../../../../Reducer/productBatchesSlice";

const ViewBatchData = ({ open, onClose, batchId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { batch, loading, error } = useSelector((state) => state.productBatches);

  const [page, setPage] = useState(1);
  const limit = 10;

  // Fetch when open or page changes
  useEffect(() => {
    if (open && batchId) {
      dispatch(fetchBatchById({ batchId, page, limit }));
    }
  }, [open, batchId, page, dispatch]);

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const metadata = batch?.metadata;

  // Function to truncate long text
  const truncateText = (text, maxLength = 30) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // ✅ Helper for CSV
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(","), // header row
      ...data.map((row) =>
        headers.map((field) => JSON.stringify(row[field] ?? "")).join(",")
      ),
    ];
    return csvRows.join("\n");
  };

  // ✅ Download All Data
  const handleDownloadAll = async () => {
    if (!metadata?.totalPages) return;

    let allData = [];
    for (let p = 1; p <= metadata.totalPages; p++) {
      const res = await dispatch(
        fetchBatchById({ batchId, page: p, limit })
      ).unwrap();

      if (res?.data) {
        const formatted = res.data.map((row) => ({
          id: row.id,
          batch_id: row.batch_id,
          ...row.dataStore, // flatten nested data
        }));
        allData = [...allData, ...formatted];
      }
    }

    // Convert to CSV
    const csv = convertToCSV(allData);

    // Trigger download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `batch_${batchId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: "85vh",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          background: theme.palette.background.default,
        },
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${alpha(
            theme.palette.primary.light,
            0.8
          )})`,
          color: "white",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        }}
      >
        {/* Left Section: Title */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <DescriptionIcon sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">
            Batch Data Details
          </Typography>
        </Box>

        {/* Right Section: Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Chip
            label={`ID: ${batchId}`}
            sx={{
              color: "white",
              backgroundColor: alpha("#fff", 0.2),
              fontWeight: "bold",
            }}
          />
          <Button
            variant="contained"
            size="small"
            sx={{ backgroundColor: "white", color: theme.palette.primary.main }}
            onClick={handleDownloadAll}
          >
            Download All
          </Button>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Loader */}
      {loading && (
        <Box
          sx={{
            textAlign: "center",
            mt: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            Loading batch data...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Paper
          elevation={0}
          sx={{ p: 3, m: 2, backgroundColor: theme.palette.error.light }}
        >
          <Typography color="error" variant="body1" align="center">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Metadata */}
      {metadata && !loading && (
        <Paper elevation={1} sx={{ p: 2, m: 2, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "center",
            }}
          >
            <Chip
              icon={<DescriptionIcon />}
              label={`Total Records: ${metadata.totalRecords}`}
              variant="outlined"
              color="primary"
            />
            <Chip
              label={`Total Pages: ${metadata.totalPages}`}
              variant="outlined"
              color="secondary"
            />
            <Chip
              label={`Current Page: ${metadata.currentPage}`}
              variant="outlined"
            />
            <Chip
              label={`Page Size: ${metadata.pageSize}`}
              variant="outlined"
            />
          </Box>
        </Paper>
      )}

      {/* Table */}
      {batch?.data?.length > 0 && !loading && (
        <>
          <Paper
            elevation={0}
            sx={{ m: 2, borderRadius: 2, overflow: "hidden" }}
          >
            <Box sx={{ overflow: "auto", maxHeight: "55vh" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "ID",
                      "BATCH ID",
                      "Event",
                      "Company",
                      "Website",
                      "Description",
                      "Employees",
                      "Address",
                      "Generic Email",
                      "Booth",
                      "First Name",
                      "Last Name",
                      "Title",
                      "Email",
                      "LinkedIn",
                      "Source URL",
                      "Leads Comment",
                      "Company Comment",
                      "Country",
                      "Industry",
                      "Location",
                      "Phone",
                      "Direct Dial",
                      "Verified",
                    ].map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#1976d2",
                          color: "#fff",
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batch.data.map((row, index) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.action.selected,
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {(page - 1) * limit + index + 1}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.batch_id}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{truncateText(row.dataStore.event_name)}</TableCell>
                      <TableCell>{truncateText(row.dataStore.company_name)}</TableCell>
                      <TableCell>
                        {row.dataStore.website && (
                          <Tooltip title={row.dataStore.website}>
                            <IconButton
                              size="small"
                              href={`https://${row.dataStore.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.dataStore.description}>
                          <span>{truncateText(row.dataStore.description, 20)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {row.dataStore.employee_count}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.dataStore.address}>
                          <span>{truncateText(row.dataStore.address, 15)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{truncateText(row.dataStore.generic_email)}</TableCell>
                      <TableCell>{row.dataStore.booth_number}</TableCell>
                      <TableCell>{row.dataStore.first_name}</TableCell>
                      <TableCell>{row.dataStore.last_name}</TableCell>
                      <TableCell>{truncateText(row.dataStore.title)}</TableCell>
                      <TableCell>
                        {row.dataStore.email && (
                          <Tooltip title={`Email: ${row.dataStore.email}`}>
                            <Chip
                              label={truncateText(row.dataStore.email, 15)}
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                window.open(`mailto:${row.dataStore.email}`, "_blank")
                              }
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.dataStore.linkedin_url && (
                          <Tooltip title={row.dataStore.linkedin_url}>
                            <IconButton
                              size="small"
                              href={row.dataStore.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.dataStore.source_url && (
                          <Tooltip title={row.dataStore.source_url}>
                            <IconButton
                              size="small"
                              href={row.dataStore.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.dataStore.leads_comment}>
                          <span>{truncateText(row.dataStore.leads_comment, 15)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.dataStore.company_comment}>
                          <span>{truncateText(row.dataStore.company_comment, 15)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{row.dataStore.country}</TableCell>
                      <TableCell>{truncateText(row.dataStore.industry)}</TableCell>
                      <TableCell>{truncateText(row.dataStore.person_location)}</TableCell>
                      <TableCell>{row.dataStore.company_phone}</TableCell>
                      <TableCell>{row.dataStore.direct_dial}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.dataStore.varified ? "Yes" : "No"}
                          size="small"
                          color={row.dataStore.varified ? "success" : "default"}
                          variant={row.dataStore.varified ? "filled" : "outlined"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={metadata?.totalPages || 1}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: "bold",
                },
              }}
            />
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default ViewBatchData;
